import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import {
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AstroAngle, AstroCityHit } from "../../../../models/interfaces";
import astrocartographyStyles from "../Astrocartography.styles";
import {
  ANGLE_LABEL_KEYS,
  ANY_ANGLE,
} from "../utils/astrocartographyConstants";
import { getPlanetGlyph } from "../utils/astrocartographyUtils";

interface AstroCityResultsProps {
  /** `undefined` until the first search runs — that is the pristine state. */
  hits?: AstroCityHit[];
  planet: string;
  angle: string;
  radiusKm: number;
}

/** Cities near the selected planet's line, nearest first, as ranked rows. */
const AstroCityResults = (props: AstroCityResultsProps) => {
  const classes = astrocartographyStyles;
  const { t } = useTranslation();
  const { hits, planet, angle, radiusKm } = props;

  const getAngleLabel = (value: string) => {
    const key = ANGLE_LABEL_KEYS[value as AstroAngle];
    return key ? (t(key) as string) : value;
  };

  const getEmptyState = (icon: JSX.Element, message: string) => (
    <Box sx={classes.emptyState}>
      {icon}
      <Typography variant="caption">{message}</Typography>
    </Box>
  );

  if (!hits) {
    return getEmptyState(
      <ExploreOutlinedIcon sx={classes.emptyIcon} />,
      t("astroResultsHint"),
    );
  }

  const lineLabel =
    angle === ANY_ANGLE ? t("astroAnyLineShort") : getAngleLabel(angle);

  return (
    <>
      <Box sx={classes.resultsSummary}>
        <Typography sx={classes.resultsCount}>{hits.length}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("astroResultsSummary", {
            planet: `${getPlanetGlyph(planet)} ${planet}`.trim(),
            radius: radiusKm,
            line: lineLabel,
          })}
        </Typography>
      </Box>

      {hits.length === 0 ? (
        getEmptyState(
          <LocationOffOutlinedIcon sx={classes.emptyIcon} />,
          t("astroNoCitiesFound", { radius: radiusKm }),
        )
      ) : (
        <List dense disablePadding sx={classes.resultsList}>
          {hits.map((hit, index) => (
            <ListItem
              key={`${hit.name}-${hit.countryCode}`}
              disableGutters
              sx={{ gap: 1, px: 0.5 }}
            >
              <Box sx={classes.cityRank}>{index + 1}</Box>
              <ListItemText
                primary={hit.name}
                secondary={`${hit.countryCode} · ${hit.angle}`}
                sx={{ my: 0 }}
              />
              <Stack sx={{ alignItems: "flex-end", flexShrink: 0 }} spacing={0.5}>
                <Typography sx={classes.cityDistance}>
                  {t("astroDistanceApprox", {
                    distance: Math.round(hit.distanceKm),
                  })}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  // Fuller bar = closer to the line.
                  value={Math.max(
                    0,
                    Math.min(100, 100 - (hit.distanceKm / radiusKm) * 100),
                  )}
                  sx={{ width: 34 }}
                />
              </Stack>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
};

export default AstroCityResults;
